/**
 * Challenges + 1v1 Duels — embedded in Community.jsx "Challenges" tab.
 * 4 preset challenge types + duel creation (random opponent in city).
 */
import { useEffect, useState } from "react";
import { Trophy, Swords, Plus, X, Users } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

const META = {
  step_streak: { color: "#3B82F6", emoji: "👟" },
  workout_30day: { color: "#F5C518", emoji: "💪" },
  protein_week: { color: "#22C55E", emoji: "🍳" },
  sleep_consistency: { color: "#A855F7", emoji: "🌙" },
};

export default function ChallengesAndDuels() {
  const [challenges, setChallenges] = useState([]);
  const [duels, setDuels] = useState([]);
  const [tab, setTab] = useState("challenges");
  const [creatingDuel, setCreatingDuel] = useState(false);

  const load = async () => {
    apiClient.get("/community/challenges-v2").then((r) => setChallenges(r.data?.challenges || []));
    apiClient.get("/community/duels").then((r) => setDuels(r.data?.duels || []));
  };
  useEffect(() => { load(); }, []);

  const join = async (type) => {
    try {
      await apiClient.post("/community/challenges-v2/join", { challenge_type: type });
      toast.success("Challenge joined!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="challenges-tab" className="px-1">
      <div style={{ display: "flex", gap: 6, padding: "4px 0 12px" }}>
        <button onClick={() => setTab("challenges")} data-testid="sub-tab-challenges"
          style={{ padding: "6px 14px", background: tab === "challenges" ? "#0C0C0B" : "transparent", color: tab === "challenges" ? "white" : "var(--text-secondary, #4A4845)", border: "1px solid", borderColor: tab === "challenges" ? "#0C0C0B" : "var(--border, rgba(12,12,11,0.16))", borderRadius: 100, fontSize: 12, fontWeight: 700 }}>
          Challenges
        </button>
        <button onClick={() => setTab("duels")} data-testid="sub-tab-duels"
          style={{ padding: "6px 14px", background: tab === "duels" ? "#0C0C0B" : "transparent", color: tab === "duels" ? "white" : "var(--text-secondary, #4A4845)", border: "1px solid", borderColor: tab === "duels" ? "#0C0C0B" : "var(--border, rgba(12,12,11,0.16))", borderRadius: 100, fontSize: 12, fontWeight: 700 }}>
          Duels
        </button>
      </div>

      {tab === "challenges" && (
        <div style={{ display: "grid", gap: 12 }}>
          {challenges.map((c) => {
            const meta = META[c.type] || {};
            const inProgress = c.participation?.status === "active";
            const pct = inProgress ? Math.min(100, ((c.participation.progress || 0) / c.target) * 100) : 0;
            return (
              <div key={c.type} data-testid={`challenge-${c.type}`} style={{ padding: 14, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderLeft: `3px solid ${meta.color}`, borderRadius: 12 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ fontSize: 24 }}>{meta.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, lineHeight: 1.05 }}>{c.label}</div>
                    <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>{c.duration_days} days · target {c.target} {c.metric}</div>
                  </div>
                  {inProgress ? (
                    <span style={{ padding: "3px 8px", background: "rgba(34,197,94,0.10)", color: "#16A34A", fontSize: 10, fontWeight: 700, borderRadius: 100, textTransform: "uppercase" }}>Active</span>
                  ) : (
                    <button onClick={() => join(c.type)} data-testid={`join-${c.type}`}
                      style={{ background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, padding: "5px 12px", fontSize: 11, fontWeight: 700 }}>
                      Join
                    </button>
                  )}
                </div>
                {inProgress && (
                  <>
                    <div style={{ marginTop: 10, height: 6, background: "var(--bg-inset, #D8D6D1)", borderRadius: 100, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: meta.color, borderRadius: 100 }} />
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-tertiary, #8A8884)", marginTop: 4 }}>{c.participation.progress || 0} / {c.target}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "duels" && (
        <div>
          <button onClick={() => setCreatingDuel(true)} data-testid="create-duel-btn"
            style={{ width: "100%", padding: 14, marginBottom: 14, background: "#0C0C0B", color: "white", border: "none", borderRadius: 14, fontFamily: "Barlow", fontSize: 13, fontWeight: 700, display: "inline-flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
            <Swords size={14} /> Challenge someone to a duel
          </button>
          {duels.length === 0 ? (
            <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", textAlign: "center", padding: 20 }}>No active duels. Start one above!</p>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {duels.map((d) => (
                <div key={d.duel_id} style={{ padding: 14, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <SmallPlayer name={d.challenger_name} score={d.challenger_score} avatar={d.challenger_avatar} />
                    <span style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 900, color: "#A52121" }}>VS</span>
                    <SmallPlayer name={d.opponent_name} score={d.opponent_score} avatar={d.opponent_avatar} />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-tertiary, #8A8884)", textAlign: "center" }}>
                    Most {d.metric} in {d.duration_days} days · {d.status}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {creatingDuel && <CreateDuelModal onClose={() => setCreatingDuel(false)} onCreated={() => { setCreatingDuel(false); load(); }} />}
    </div>
  );
}

function SmallPlayer({ name, score, avatar }) {
  return (
    <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
      {avatar ? <img src={avatar} alt="" width={36} height={36} style={{ borderRadius: "50%", margin: "0 auto" }} /> : (
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--bg-inset, #D8D6D1)", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 14, margin: "0 auto" }}>{(name || "?").slice(0, 1).toUpperCase()}</div>
      )}
      <div style={{ fontSize: 11, fontWeight: 700, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name || "Member"}</div>
      <div style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 900, lineHeight: 1 }}>{score || 0}</div>
    </div>
  );
}

function CreateDuelModal({ onClose, onCreated }) {
  const [metric, setMetric] = useState("workouts");
  const [duration, setDuration] = useState(7);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await apiClient.post("/community/duels", { metric, duration_days: duration });
      toast.success("Duel started! Opponent matched in your city.");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-end bg-black/50" onClick={onClose} data-testid="create-duel-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full bg-white rounded-t-[20px] p-6">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>Start a Duel</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", marginTop: 4 }}>We'll match you with a random member in your city.</p>
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          <select value={metric} onChange={(e) => setMetric(e.target.value)} data-testid="duel-metric"
            style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14 }}>
            <option value="workouts">Most workouts</option>
            <option value="steps">Most steps</option>
            <option value="reps">Most reps</option>
          </select>
          <select value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10))} data-testid="duel-duration"
            style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14 }}>
            <option value={3}>3 days</option><option value={7}>7 days</option><option value={14}>14 days</option>
          </select>
          <button onClick={submit} disabled={busy} data-testid="duel-submit"
            style={{ height: 48, background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontSize: 14, fontWeight: 800 }}>
            {busy ? "Matching…" : "Start duel"}
          </button>
        </div>
      </div>
    </div>
  );
}
